/**
 * The full criteria table with drag-and-drop reordering support.
 *
 * Wraps the table in DndContext + SortableContext and renders:
 * - Column headers (with drag handle column when editable)
 * - SortableCriterionRow for each existing criterion
 * - Inline add-row when the user is adding a new criterion
 * - Empty-state row when no criteria exist
 */
import { useCallback } from "react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { EvaluationCriterion } from "../../../../types/criteria.type";
import type { CriterionFormValues } from "./criteria-form.types";
import { SortableCriterionRow } from "./SortableCriterionRow";
import { CriterionFormCells } from "./CriterionFormCells";

interface CriteriaTableProps {
	canEdit: boolean;
	criteria: EvaluationCriterion[];
	editingId: number | null;
	formValues: CriterionFormValues;
	isAdding: boolean;
	isSaving: boolean;
	onSetFormValues: React.Dispatch<React.SetStateAction<CriterionFormValues>>;
	onCancel: () => void;
	onDelete: (id: number) => void;
	onReorder: (fromIndex: number, toIndex: number) => void;
	onSaveEdit: () => void;
	onSaveNew: () => void;
	onStartEdit: (criterion: EvaluationCriterion) => void;
	onUpdateField: (field: keyof CriterionFormValues, value: string) => void;
}

export const CriteriaTable: React.FC<CriteriaTableProps> = ({
	criteria,
	canEdit,
	isAdding,
	editingId,
	isSaving,
	formValues,
	onUpdateField,
	onSetFormValues,
	onSaveNew,
	onSaveEdit,
	onCancel,
	onStartEdit,
	onDelete,
	onReorder,
}) => {
	// DnD sensors — require 5px movement to start dragging (prevents accidental drags on click)
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	const sortableIds = criteria.map((c) => c.id);
	const isDraggable = canEdit && !isAdding && editingId === null;
	const colCount = canEdit ? 6 : 4;

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;
			if (!over || active.id === over.id) return;

			const oldIndex = criteria.findIndex((c) => c.id === active.id);
			const newIndex = criteria.findIndex((c) => c.id === over.id);
			if (oldIndex !== -1 && newIndex !== -1) {
				onReorder(oldIndex, newIndex);
			}
		},
		[criteria, onReorder],
	);

	return (
		<div className="overflow-x-auto">
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleDragEnd}
			>
				<table className="w-full text-sm" data-testid="criteria-table">
					<thead>
						<tr className="border-b border-gray-200 text-left text-gray-500">
							{canEdit && <th className="w-8 pb-2 font-medium" />}
							<th className="pb-2 pr-4 font-medium">Name</th>
							<th className="pb-2 pr-4 font-medium">Description</th>
							<th className="pb-2 pr-4 font-medium">Type</th>
							<th className="pb-2 pr-4 font-medium text-right">Weight (%)</th>
							{canEdit && <th className="pb-2 font-medium text-right">Actions</th>}
						</tr>
					</thead>
					<SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
						<tbody>
							{/* Empty state */}
							{criteria.length === 0 && !isAdding && (
								<tr>
									<td colSpan={colCount} className="py-6 text-center text-sm text-gray-400">
										No criteria defined yet. Click &quot;Add Criterion&quot; below to get started.
									</td>
								</tr>
							)}

							{/* Existing rows */}
							{criteria.map((criterion) => (
								<SortableCriterionRow
									key={criterion.id}
									criterion={criterion}
									canEdit={canEdit}
									isEditing={canEdit && editingId === criterion.id}
									isSaving={isSaving}
									formValues={formValues}
									onUpdateField={onUpdateField}
									onSetFormValues={onSetFormValues}
									onSaveEdit={onSaveEdit}
									onCancel={onCancel}
									onStartEdit={onStartEdit}
									onDelete={onDelete}
									isDraggable={isDraggable}
								/>
							))}

							{/* Inline add row */}
							{canEdit && isAdding && (
								<tr className="border-b border-blue-200 bg-blue-50/50" data-testid="add-criterion-row">
									<td className="w-8 py-2 pr-1" />
									<CriterionFormCells
										formValues={formValues}
										isSaving={isSaving}
										onUpdateField={onUpdateField}
										onSetFormValues={onSetFormValues}
										onSave={onSaveNew}
										onCancel={onCancel}
										testIdPrefix="add"
										autoFocusName
									/>
								</tr>
							)}
						</tbody>
					</SortableContext>
				</table>
			</DndContext>
		</div>
	);
};
